import { forwardRef, useContext } from 'react';
import { NavIcon } from 'components/Kizen/Icon';
import { DEFAULT_ITEM_IDS } from 'pages/Account/pages/Toolbar/constants';
import {
  Child,
  ChildrenContainer,
  IconContainer,
  StyledKDSIcon,
} from './styles';
import { NavLink } from 'react-router-dom';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import { getLabel, getQAForItem, getRoute } from './util';
import { PluginContext } from 'ts-components/Plugins/context';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import { useTranslation } from 'react-i18next';
import { useLogActivityContext } from 'ts-activities/LogActivityContext';
import { useGenericAppCustomScript } from '@kizenapps/engine/react';

const NavigationChildren = forwardRef(
  (
    {
      item,
      mobile,
      chosenBusinessName,
      readOnly,
      onClickLogout,
      translationKeys,
      offset,
      shouldShrinkToLeft,
      close,
    },
    forwardedRef
  ) => {
    const { toolbarItemsByFullId } = useContext(PluginContext);
    const { t } = useTranslation();
    const { openLogActivityModal } = useLogActivityContext();

    const [showToast] = useToast();
    const [executeInline, { pending: inlinePending }] =
      useGenericAppCustomScript({
        onError: (e) => {
          showToast({
            message: `${t('Script could not be executed')}: ${e?.message}`,
            variant: toastVariant.FAILURE,
          });
        },
      });

    return (
      <ChildrenContainer
        collapse={item.children.every((c) => c.widthPercent !== 50)}
        ref={forwardedRef}
        mobile={mobile}
        offset={offset}
        shouldShrinkToLeft={shouldShrinkToLeft}
      >
        {item.id === DEFAULT_ITEM_IDS.MY_ACCOUNT ? (
          <TextEllipsisWithTooltip
            color="var(--color-font-primary)"
            type="small"
            as="span"
            weight="bold"
            style={{
              lineHeight: '100%',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap',
            }}
          >
            {chosenBusinessName}
          </TextEllipsisWithTooltip>
        ) : null}
        {item.children.map((child) => {
          const dynamicItem = toolbarItemsByFullId[child.id];
          const childRoute = getRoute(child, dynamicItem);

          let onClick = childRoute.onClick;

          if (readOnly) {
            onClick = undefined;
          } else if (child.id === DEFAULT_ITEM_IDS.LOGOUT) {
            onClick = onClickLogout;
          } else if (child.plugin_api_name) {
            onClick = (ev) => {
              ev.stopPropagation();

              if (inlinePending) {
                return false;
              }

              const dynamicItem = toolbarItemsByFullId[child.id];

              if (dynamicItem?.script) {
                executeInline(
                  dynamicItem.script,
                  dynamicItem.args,
                  dynamicItem
                );
              }

              return false;
            };
          } else if (child.activityId) {
            onClick = (ev) => {
              ev.stopPropagation();
              openLogActivityModal(child.activityId);
              close();
            };
          }

          return (
            <Child
              key={child.id}
              full={child.widthPercent !== 50}
              as={
                readOnly ||
                !childRoute.path ||
                child.id === DEFAULT_ITEM_IDS.LOGOUT ||
                (child.plugin_api_name && dynamicItem?.script)
                  ? 'div'
                  : childRoute.external
                    ? 'a'
                    : NavLink
              }
              to={childRoute.external ? null : childRoute.path}
              href={childRoute.external ? childRoute.path : null}
              exact={childRoute.exact}
              target={childRoute.target}
              rel={childRoute.rel}
              onClick={onClick}
              {...getQAForItem(child, childRoute)}
            >
              <IconContainer className={child.id}>
                {(() => {
                  const dynamicItem = toolbarItemsByFullId[child.id];

                  // The child icon will only be a string when it's never been overridden
                  // so we fall back to the dynamic icon in that case so it updates if the plugin is updated
                  if (dynamicItem && typeof child.icon === 'string') {
                    return (
                      <StyledKDSIcon
                        icon={`custom-${dynamicItem.icon}`}
                        className="dynamic-child-item"
                      />
                    );
                  }
                  return <NavIcon icon={child.icon} />;
                })()}
              </IconContainer>
              <TextEllipsisWithTooltip>
                {getLabel(child, translationKeys)}
              </TextEllipsisWithTooltip>
            </Child>
          );
        })}
      </ChildrenContainer>
    );
  }
);

export default NavigationChildren;
